import React from "react";

/**
 * ASCII Design System
 *
 * A retro, terminal-inspired design system with ASCII art borders and boxes.
 * Consistent with the plaintext notes aesthetic.
 */

// ============================================================================
// Theme Constants
// ============================================================================

export const ASCII_THEME = {
  colors: {
    primary: "#000000",
    background: "#ffffff",
    accent: "#fbf1c7", // Warm beige/cream
    border: "#000000",
    error: "#dc2626",
    success: "#16a34a",
    muted: "#6b7280",
  },
  spacing: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "0.75rem",
    lg: "1rem",
    xl: "1.5rem",
  },
  fontSize: {
    xs: "0.6rem",
    sm: "0.75rem",
    base: "0.875rem",
    lg: "1rem",
    xl: "1.25rem",
  },
  borderChars: {
    // Box drawing characters
    topLeft: "┌",
    topRight: "┐",
    bottomLeft: "└",
    bottomRight: "┘",
    horizontal: "─",
    vertical: "│",
    // Double line characters
    doubleTopLeft: "╔",
    doubleTopRight: "╗",
    doubleBottomLeft: "╚",
    doubleBottomRight: "╝",
    doubleHorizontal: "═",
    doubleVertical: "║",
  },
};

// ============================================================================
// ASCII Box Components
// ============================================================================

interface AsciiBoxProps {
  children: React.ReactNode;
  title?: string;
  width?: number; // Character width
  style?: "single" | "double";
  className?: string;
}

/**
 * Creates an ASCII box with optional title
 */
export const AsciiBox: React.FC<AsciiBoxProps> = ({
  children,
  title,
  width = 65,
  style = "single",
  className = "",
}) => {
  const chars = ASCII_THEME.borderChars;
  const isDouble = style === "double";

  const tl = isDouble ? chars.doubleTopLeft : chars.topLeft;
  const tr = isDouble ? chars.doubleTopRight : chars.topRight;
  const bl = isDouble ? chars.doubleBottomLeft : chars.bottomLeft;
  const br = isDouble ? chars.doubleBottomRight : chars.bottomRight;
  const h = isDouble ? chars.doubleHorizontal : chars.horizontal;

  const topBorder = title
    ? `${tl}${h} ${title.toUpperCase()} ${h.repeat(
        Math.max(0, width - title.length - 4)
      )}${tr}`
    : `${tl}${h.repeat(width)}${tr}`;

  const bottomBorder = `${bl}${h.repeat(width)}${br}`;

  return (
    <div className={`font-mono ${className}`}>
      <pre className="text-[0.6rem] sm:text-xs mb-0 select-none">
        {topBorder}
      </pre>
      <div className="border-l-2 border-r-2 border-black px-2 sm:px-4 py-2 sm:py-3">
        {children}
      </div>
      <pre className="text-[0.6rem] sm:text-xs mb-0 select-none">
        {bottomBorder}
      </pre>
    </div>
  );
};

/**
 * Creates a simple inline ASCII box for small content
 */
export const AsciiInlineBox: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = "" }) => {
  return (
    <span className={`border-2 border-black px-2 py-1 ${className}`}>
      {children}
    </span>
  );
};

// ============================================================================
// ASCII Header Component
// ============================================================================

interface AsciiHeaderProps {
  title: string;
  subtitle?: string;
  fullWidth?: boolean;
}

export const AsciiHeader: React.FC<AsciiHeaderProps> = ({
  title,
  subtitle,
  fullWidth = true,
}) => {
  const width = fullWidth ? 65 : 35;
  const titlePadding = Math.floor((width - title.length - 4) / 2);
  const titleLine = `│${" ".repeat(titlePadding)}★ ${title} ★${" ".repeat(
    width - title.length - titlePadding - 4
  )}│`;

  return (
    <>
      {/* Desktop Header */}
      <pre className="hidden sm:block text-xs mb-4 select-none">
        {`┌${"─".repeat(width)}┐
${titleLine}${
          subtitle
            ? `\n│${subtitle
                .padStart((width + subtitle.length) / 2)
                .padEnd(width)}│`
            : ""
        }
└${"─".repeat(width)}┘`}
      </pre>

      {/* Mobile Header */}
      <pre className="sm:hidden text-[0.6rem] mb-3 select-none">
        {`┌${"─".repeat(35)}┐
│${`★ ${title} ★`.padStart(20).padEnd(35)}│${
          subtitle ? `\n│${subtitle.padStart(20).padEnd(35)}│` : ""
        }
└${"─".repeat(35)}┘`}
      </pre>
    </>
  );
};

// ============================================================================
// ASCII Button Components
// ============================================================================

interface AsciiButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "primary" | "danger" | "ghost";
  size?: "sm" | "md" | "lg";
  children: React.ReactNode;
}

export const AsciiButton: React.FC<AsciiButtonProps> = ({
  variant = "default",
  size = "md",
  children,
  className = "",
  disabled,
  ...props
}) => {
  const baseStyles =
    "cursor-pointer border-2 border-black font-mono transition-colors uppercase tracking-wide";

  const sizeStyles = {
    sm: "px-2 py-1 text-[0.65rem] sm:text-xs",
    md: "px-3 py-1.5 text-xs sm:text-sm",
    lg: "px-4 py-2 text-sm sm:text-base",
  };

  const variantStyles = {
    default:
      "bg-white hover:bg-black hover:text-white disabled:opacity-30 disabled:cursor-not-allowed",
    primary:
      "bg-black text-white hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed",
    danger:
      "bg-white hover:bg-red-600 hover:text-white hover:border-red-600 disabled:opacity-30 disabled:cursor-not-allowed",
    ghost:
      "border-black bg-transparent hover:bg-[#fbf1c7] disabled:opacity-30 disabled:cursor-not-allowed",
  };

  return (
    <button
      className={`${baseStyles} ${sizeStyles[size]} ${variantStyles[variant]} ${className}`}
      disabled={disabled}
      {...props}
    >
      [ {children} ]
    </button>
  );
};

// ============================================================================
// ASCII Panel/Section Components
// ============================================================================

interface AsciiPanelProps {
  title?: string;
  children: React.ReactNode;
  className?: string;
}

export const AsciiPanel: React.FC<AsciiPanelProps> = ({
  title,
  children,
  className = "",
}) => {
  return (
    <div className={`border-2 border-black ${className}`}>
      {title && (
        <div className="bg-[#fbf1c7] border-b-2 border-black px-3 sm:px-4 py-2">
          <div className="text-xs sm:text-sm font-bold tracking-wide uppercase">
            {title}
          </div>
        </div>
      )}
      <div className="p-3 sm:p-4">{children}</div>
    </div>
  );
};

// ============================================================================
// ASCII Status Badge
// ============================================================================

interface AsciiStatusBadgeProps {
  status: "idle" | "active" | "error" | "success";
  children: React.ReactNode;
  className?: string;
}

export const AsciiStatusBadge: React.FC<AsciiStatusBadgeProps> = ({
  status,
  children,
  className = "",
}) => {
  const statusStyles = {
    idle: "text-gray-600",
    active: "text-green-600",
    error: "text-red-600",
    success: "text-green-600",
  };

  const statusIcon = {
    idle: "○",
    active: "●",
    error: "✗",
    success: "✓",
  };

  return (
    <span className={`font-mono text-xs ${statusStyles[status]} ${className}`}>
      {statusIcon[status]} {children}
    </span>
  );
};

// ============================================================================
// ASCII Divider
// ============================================================================

export const AsciiDivider: React.FC<{
  className?: string;
}> = ({ className = "" }) => {
  return (
    <div className={`border-t-2 border-black my-3 sm:my-4 ${className}`} />
  );
};

// ============================================================================
// ASCII List Item
// ============================================================================

interface AsciiListItemProps {
  title: string;
  subtitle?: string;
  isActive?: boolean;
  onClick?: () => void;
  className?: string;
}

export const AsciiListItem: React.FC<AsciiListItemProps> = ({
  title,
  subtitle,
  isActive = false,
  onClick,
  className = "",
}) => {
  const titleLength = window.innerWidth < 640 ? 25 : 31;
  const displayTitle = title.substring(0, titleLength).padEnd(titleLength, " ");
  const displaySubtitle = subtitle
    ? subtitle.substring(0, titleLength).padEnd(titleLength, " ")
    : "";

  return (
    <button
      onClick={onClick}
      className={`w-full text-left border-2 transition-colors cursor-pointer ${
        isActive
          ? "bg-[#fbf1c7] border-black"
          : "border-black hover:bg-[#fbf1c7]"
      } ${className}`}
    >
      {/* Desktop */}
      <pre className="hidden sm:block p-2 text-[0.65rem] font-mono">
        {`┌─────────────────────────────────┐
│ ${displayTitle} │${displaySubtitle ? `\n│ ${displaySubtitle} │` : ""}
└─────────────────────────────────┘`}
      </pre>
      {/* Mobile */}
      <pre className="sm:hidden p-2 text-[0.55rem] font-mono">
        {`┌───────────────────────────┐
│ ${displayTitle} │${displaySubtitle ? `\n│ ${displaySubtitle} │` : ""}
└───────────────────────────┘`}
      </pre>
    </button>
  );
};

// ============================================================================
// ASCII Modal
// ============================================================================

interface AsciiModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

export const AsciiModal: React.FC<AsciiModalProps> = ({
  isOpen,
  title,
  children,
  footer,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-white bg-opacity-40 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white border-4 border-black font-mono max-w-md w-full shadow-2xl mx-2">
        {/* Desktop Modal Header */}
        <pre className="hidden sm:block p-4 text-xs border-b-2 border-black bg-[#fbf1c7]">
          {`╔═══════════════════════════════════════╗
║          ${title.toUpperCase().padEnd(25)}║
╚═══════════════════════════════════════╝`}
        </pre>

        {/* Mobile Modal Header */}
        <div className="sm:hidden p-3 text-xs border-b-2 border-black bg-[#fbf1c7] text-center font-bold">
          {title.toUpperCase()}
        </div>

        <div className="p-4 sm:p-6 text-xs sm:text-sm">{children}</div>

        {footer && (
          <div className="border-t-2 border-black p-3 sm:p-4">{footer}</div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// ASCII Input
// ============================================================================

interface AsciiInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
}

export const AsciiInput: React.FC<AsciiInputProps> = ({
  label,
  className = "",
  ...props
}) => {
  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-xs uppercase tracking-wide font-mono opacity-60">
          {label}
        </label>
      )}
      <input
        className={`w-full border-2 border-black px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-mono focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 ${className}`}
        {...props}
      />
    </div>
  );
};

// ============================================================================
// ASCII Textarea
// ============================================================================

interface AsciiTextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
}

export const AsciiTextarea: React.FC<AsciiTextareaProps> = ({
  label,
  className = "",
  ...props
}) => {
  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-xs uppercase tracking-wide font-mono opacity-60">
          {label}
        </label>
      )}
      <textarea
        className={`w-full border-2 border-black px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-mono focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 resize-none ${className}`}
        {...props}
      />
    </div>
  );
};

// ============================================================================
// ASCII Toggle/Tabs
// ============================================================================

interface AsciiTabsProps {
  tabs: { id: string; label: string }[];
  activeTab: string;
  onChange: (tabId: string) => void;
  disabled?: boolean;
  className?: string;
}

export const AsciiTabs: React.FC<AsciiTabsProps> = ({
  tabs,
  activeTab,
  onChange,
  disabled = false,
  className = "",
}) => {
  return (
    <div className={`flex gap-0 ${className}`}>
      {tabs.map((tab, index) => (
        <button
          key={tab.id}
          onClick={() => !disabled && onChange(tab.id)}
          disabled={disabled}
          className={`px-4 sm:px-6 py-2 sm:py-3 border-2 border-black font-medium text-xs sm:text-sm uppercase tracking-wide transition-colors
            ${index > 0 ? "border-l-0" : ""}
            ${
              activeTab === tab.id
                ? "bg-black text-white"
                : "bg-white hover:bg-[#fbf1c7] disabled:bg-gray-100"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};

// ============================================================================
// ASCII Loading Indicator
// ============================================================================

export const AsciiLoader: React.FC<{
  text?: string;
  className?: string;
}> = ({ text = "Loading", className = "" }) => {
  return (
    <div className={`font-mono text-xs ${className}`}>
      <span className="animate-pulse">⟳ {text}...</span>
    </div>
  );
};
